
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Watch } from "vue-property-decorator";
    import { Chart } from "chart.js";
    import * as toastr from "toastr";
    import Utils from "@/utilities/Utils";
    import ApiClient from "@/utilities/ApiClient";
    import DatePicker from "@/vue/components/DatePicker.vue";
    import { VisitReportParameters } from "@/model/VisitReportParameters";
    import { IVisitReportPathway, VisitReportPathway } from "@/model/VisitReportPathway";
    import { ISiteLookup } from "@/model/Site";
    import { ILookupItem, LookupItem } from "@/model/LookupItem";
    import { UserRole } from "@/model/Enums";
    import { IClientLookup } from "@/model/Client";

    @Component({
        components: { DatePicker }
    })
    export default class VisitReport extends Vue {

        async mounted(): Promise<void> {
            await Promise.all([
                (async () => { this.clientList = await ApiClient.get(Utils.addClientQueryParameters("/api/client/lookups")); })(),
                (async () => { this.siteList = await ApiClient.get(Utils.addClientQueryParameters("/api/site/lookups")); })()
            ]);
            this.initialiseChart();
            await this.loadChartData();
        }

        searchParameters = new VisitReportParameters();

        get canFilterClient(): boolean { return this.$store.state.signedInUser?.canFilterClient === true; }
        get canFilterSite(): boolean { return this.$store.state.signedInUser?.canFilterSite === true; }
        get canFilterPathway(): boolean { return this.pathwayList.length > 1; }

        clientList: Array<IClientLookup> = [];
        get clientOptions(): Array<IClientLookup> { return Utils.clientOptionsFilter(this.clientList); }
        siteList: Array<ISiteLookup> = [];
        get siteOptions(): Array<ISiteLookup> { return Utils.siteOptionsFilter(this.siteList, this.searchParameters.clientID); }
        pathwayList: Array<ILookupItem> = [];
        get pathwayOptions(): Array<ILookupItem> { return Utils.optionsFilter(this.pathwayList); }
        numberOfMonthsOptions = [
            LookupItem.createLookup(12, "Last 12 months"),
            LookupItem.createLookup(6, "Last 6 months"),
            LookupItem.createLookup(3, "Last 3 months")
        ]

        chart?: Chart

        initialiseChart(): void {
            const ctx = (this.$refs.briefingsChart as HTMLCanvasElement).getContext('2d') as CanvasRenderingContext2D;
            const options: Chart.ChartConfiguration = {
                type: "bar",
                data: {
                    labels: [],
                    datasets: [] },
                options: {
                    scales: { xAxes: [{ stacked: true }], yAxes: [{ stacked: true }] },
                    responsive: true }
            }
            this.chart = new Chart(ctx, options);
        }

        async loadChartData(): Promise<void> {
            if(!this.chart) return;
            const pathwaysData = await ApiClient.post("/api/report/visits", this.searchParameters);
            const pathways: Array<VisitReportPathway> = pathwaysData.map((m: IVisitReportPathway) => new VisitReportPathway(m));

            if(!pathways || pathways.length == 0) {
                console.warn("No data : ", pathways);
                toastr.warning("No data to show!");
                this.chart.data = { labels: [], datasets: [] };
                this.chart.update();
                return;
            }

            // https://learnui.design/tools/data-color-picker.html#palette
            //const colours = ["#003f5c","#2f4b7c","#665191","#a05195","#d45087","#f95d6a","#ff7c43","#ffa600"];
            //const colours = ["#ffa600","#ff7c43","#f95d6a","#d45087","#a05195","#665191","#2f4b7c","#003f5c"];
            const colours = ["#003f5c","#58508d","#bc5090","#ff6361","#ffa600"];
            const datasets: Array<Chart.ChartDataSets> = pathways.map((pathway, i) => ({
                    data: pathway.months.map(m => m.count),
                    label: pathway.description,
                    backgroundColor: colours[i % colours.length]
                }));
            const labels: Array<string> = pathways[0].months.map(m => m.monthName);

            this.chart.data = { labels, datasets };
            this.chart.update();
        }

        @Watch("searchParameters.clientID")
        async onClientchanged(): Promise<void> {
            this.pathwayList = [];
            await this.loadChartData();
        }

        @Watch("searchParameters.siteID")
        async onSitechanged(): Promise<void> {
            await this.loadPathways();
            await this.loadChartData();
        }

        @Watch("searchParameters.pathwayID")
        async onPathwaychanged(): Promise<void> {
            await this.loadChartData();
        }

        @Watch("searchParameters.numberOfMonths")
        async onNumberOfMonthschanged(): Promise<void> {
            await this.loadChartData();
        }

        // sites are filtered on client but pathways filtered on server (hmm??)
        async loadPathways(): Promise<void> {
            if(!this.searchParameters.siteID) {
                this.pathwayList = [];
                return;
            }
            this.pathwayList = await ApiClient.get(`/api/pathway/lookups?siteID=${this.searchParameters.siteID}`);
        }
    }
